import React, { useEffect, useRef, useState } from "react";
import { ChevronDown, Globe, Search, X } from "lucide-react";

const TimezoneDropdown = ({
	value,
	onChange,
	isOpen,
	setIsOpen,
	date,
	startTime,
	endTime,
}) => {
	const [searchQuery, setSearchQuery] = useState("");
	const dropdownRef = useRef(null);
	const searchInputRef = useRef(null);

	// Common timezone mapping with friendly names
	const commonTimezones = [
		{ id: "America/New_York", name: "Eastern Time (ET)", shorthand: "ET" },
		{ id: "America/Chicago", name: "Central Time (CT)", shorthand: "CT" },
		{ id: "America/Denver", name: "Mountain Time (MT)", shorthand: "MT" },
		{ id: "America/Los_Angeles", name: "Pacific Time (PT)", shorthand: "PT" },
		{ id: "America/Anchorage", name: "Alaska Time (AKT)", shorthand: "AKT" },
		{ id: "Pacific/Honolulu", name: "Hawaii Time (HT)", shorthand: "HT" },
		{ id: "Europe/London", name: "British Time (BST/GMT)", shorthand: "BST" },
		{
			id: "Europe/Paris",
			name: "Central European Time (CET)",
			shorthand: "CET",
		},
		{ id: "Asia/Dubai", name: "Gulf Time (GT)", shorthand: "GT" },
		{ id: "Asia/Shanghai", name: "China Time (CT)", shorthand: "CST" },
		{ id: "Asia/Tokyo", name: "Japan Time (JST)", shorthand: "JST" },
		{
			id: "Australia/Sydney",
			name: "Australian Eastern Time (AET)",
			shorthand: "AET",
		},
	];

	// Get the current user's timezone
	const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

	// Filter timezones based on search query
	const filteredTimezones = commonTimezones.filter(
		(tz) =>
			tz.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
			tz.shorthand.toLowerCase().includes(searchQuery.toLowerCase()) ||
			tz.id.toLowerCase().includes(searchQuery.toLowerCase())
	);

	// Validate date and time format
	const isValidDateTime = (date, time) => {
		if (!date || !time) return false;

		try {
			const [hours, minutes] = time.split(":");
			const [year, month, day] = date.split("-");

			if (!hours || !minutes || !year || !month || !day) return false;

			const testDate = new Date(
				parseInt(year),
				parseInt(month) - 1,
				parseInt(day),
				parseInt(hours),
				parseInt(minutes)
			);

			return !isNaN(testDate.getTime());
		} catch {
			return false;
		}
	};

	// Function to convert time to timezone
	const convertToTimezone = (date, time, fromTZ, toTZ) => {
		if (!isValidDateTime(date, time)) {
			return "--:--";
		}

		try {
			const [hours, minutes] = time.split(":");
			const [year, month, day] = date.split("-");

			const localDate = new Date(
				parseInt(year),
				parseInt(month) - 1,
				parseInt(day),
				parseInt(hours),
				parseInt(minutes)
			);

			const formatter = new Intl.DateTimeFormat("en-US", {
				timeZone: toTZ,
				hour: "numeric",
				minute: "2-digit",
				hour12: true,
			});

			return formatter.format(localDate);
		} catch (error) {
			console.error("Time conversion error:", error);
			return "--:--";
		}
	};

	// Get day offset for timezone
	const getDayOffset = (date, time, fromTZ, toTZ) => {
		if (!isValidDateTime(date, time)) {
			return "";
		}

		try {
			const [hours, minutes] = time.split(":");
			const [year, month, day] = date.split("-");

			const localDate = new Date(
				parseInt(year),
				parseInt(month) - 1,
				parseInt(day),
				parseInt(hours),
				parseInt(minutes)
			);

			const fromFormatter = new Intl.DateTimeFormat("en-US", {
				timeZone: fromTZ,
				day: "numeric",
			});

			const toFormatter = new Intl.DateTimeFormat("en-US", {
				timeZone: toTZ,
				day: "numeric",
			});

			const fromDay = parseInt(fromFormatter.format(localDate));
			const toDay = parseInt(toFormatter.format(localDate));

			if (toDay > fromDay) return "+1";
			if (toDay < fromDay) return "-1";
			return "";
		} catch (error) {
			console.error("Day offset calculation error:", error);
			return "";
		}
	};

	// Function to get timezone display info
	const getTimezoneInfo = (timezoneId) => {
		const timezone = commonTimezones.find((tz) => tz.id === timezoneId);
		if (!timezone) return { name: timezoneId, time: "", dayOffset: "" };

		if (!isValidDateTime(date, startTime) || !isValidDateTime(date, endTime)) {
			return {
				name: timezone.name,
				time: "--:-- - --:--",
				dayOffset: "",
			};
		}

		const startConverted = convertToTimezone(
			date,
			startTime,
			userTimezone,
			timezoneId
		);
		const endConverted = convertToTimezone(
			date,
			endTime,
			userTimezone,
			timezoneId
		);
		const dayOffset = getDayOffset(date, startTime, userTimezone, timezoneId);

		return {
			name: timezone.name,
			time: `${startConverted} - ${endConverted}`,
			dayOffset,
		};
	};

	const selectedTimezone = getTimezoneInfo(value);

	useEffect(() => {
		const handleClickOutside = (event) => {
			if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
				setIsOpen(false);
				setSearchQuery("");
			}
		};

		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, [setIsOpen]);

	return (
		<div className="relative w-full" ref={dropdownRef}>
			<button
				type="button"
				onClick={() => setIsOpen(!isOpen)}
				className="w-full flex items-center justify-between px-3 py-2 hover:bg-gray-100/50 dark:hover:bg-gray-800/50 rounded-lg text-sm text-gray-600 dark:text-gray-300"
			>
				<div className="flex items-center gap-2 min-w-0 flex-shrink">
					<Globe className="w-4 h-4 text-gray-500 dark:text-gray-400 flex-shrink-0" />
					<span className="truncate text-sm">{selectedTimezone.name}</span>
				</div>
				<div className="flex items-center gap-2 flex-shrink-0 ml-2">
					<span className="text-gray-500 dark:text-gray-400 text-sm">
						{selectedTimezone.time}
					</span>
					{selectedTimezone.dayOffset && (
						<span className="text-xs text-gray-400 dark:text-gray-500">
							({selectedTimezone.dayOffset} day)
						</span>
					)}
					<ChevronDown className="w-4 h-4 text-gray-500 dark:text-gray-400 flex-shrink-0" />
				</div>
			</button>

			{isOpen && (
				<div className="absolute z-50 mt-1 w-full max-h-80 overflow-hidden rounded-md bg-white dark:bg-gray-800 shadow-lg ring-1 ring-black ring-opacity-5">
					<div className="sticky top-0 z-10 p-2 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
						<div className="relative">
							<Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" />
							<input
								ref={searchInputRef}
								type="text"
								value={searchQuery}
								onChange={(e) => setSearchQuery(e.target.value)}
								placeholder="Search timezones (e.g. PT, EST)..."
								className="w-full pl-9 pr-8 py-1.5 bg-gray-100 dark:bg-gray-700/50 text-[16px] sm:text-sm text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-500"
							/>
							{searchQuery && (
								<button
									onClick={() => setSearchQuery("")}
									className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-full"
								>
									<X className="w-4 h-4 text-gray-500 dark:text-gray-400" />
								</button>
							)}
						</div>
					</div>

					<div className="overflow-auto max-h-64 py-1 scrollbar scrollbar-track-transparent scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600 hover:scrollbar-thumb-gray-300 dark:hover:scrollbar-thumb-gray-600">
						{filteredTimezones.length === 0 ? (
							<div className="px-4 py-1.5 text-sm text-gray-500 dark:text-gray-400">
								No timezones found
							</div>
						) : (
							filteredTimezones.map((timezone) => {
								const info = getTimezoneInfo(timezone.id);

								return (
									<div
										key={timezone.id}
										onClick={() => {
											onChange(timezone.id);
											setIsOpen(false);
											setSearchQuery("");
										}}
										className={`cursor-pointer px-4 py-1.5 text-sm hover:bg-gray-100 dark:hover:bg-gray-700
						  ${
								value === timezone.id
									? "bg-blue-50 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400"
									: "text-gray-900 dark:text-gray-100"
							}`}
									>
										<div className="flex items-center justify-between min-w-0">
											<span className="truncate flex-shrink mr-3">
												{timezone.name}
											</span>
											<span className="text-gray-500 dark:text-gray-400 whitespace-nowrap flex-shrink-0">
												{info.time}
												{info.dayOffset && (
													<span className="ml-1 text-xs text-gray-400 dark:text-gray-500">
														({info.dayOffset} day)
													</span>
												)}
											</span>
										</div>
									</div>
								);
							})
						)}
					</div>
				</div>
			)}
		</div>
	);
};

export default TimezoneDropdown;
